import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import { AVATARS, getLocalizedAvatarInfo } from '../../config/avatarMapping';
import {
  type Conversation,
  deleteConversation,
  getUserConversations,
  updateConversation,
} from '../../services/chatService';
import { isAuthenticated } from '../../services/cognitoService';
import './CompanionProfile.css';

interface ChatSession {
  id: string;
  title: string;
  date: string;
  preview: string;
}

// Helper to format relative date
const formatRelativeDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return 'Last week';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  } catch {
    return '';
  }
};

const CompanionProfile: React.FC = () => {
  const { companionId } = useParams<{ companionId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Rename modal state
  const [renameModal, setRenameModal] = useState<{
    isOpen: boolean;
    session: ChatSession | null;
    newTitle: string;
  }>({
    isOpen: false,
    session: null,
    newTitle: '',
  });
  const [isRenaming, setIsRenaming] = useState(false);

  // Check authentication status
  useEffect(() => {
    setIsLoggedIn(isAuthenticated());
  }, []);

  // Get companion data from avatar mapping
  const companion = useMemo(() => {
    if (!companionId) return null;
    const avatar = AVATARS[companionId];
    if (!avatar) return null;
    return getLocalizedAvatarInfo(avatar, i18n.language);
  }, [companionId, i18n.language]);

  // Load chat history for this specific sahabe
  useEffect(() => {
    const loadHistory = async () => {
      if (!companionId || !isLoggedIn) {
        setChatSessions([]);
        return;
      }

      setIsLoadingHistory(true);
      try {
        // Get all user conversations and filter by avatarId
        const { conversations } = await getUserConversations();

        // Filter conversations for this specific avatar
        const avatarConversations = conversations.filter(
          (conv: Conversation) => conv.avatarId === companionId,
        );

        // Convert to ChatSession format
        const sessions: ChatSession[] = avatarConversations.map((conv: Conversation) => ({
          id: conv.id,
          title: conv.title || 'Untitled conversation',
          date: formatRelativeDate(conv.lastMessageAt),
          preview: conv.lastMessagePreview || '',
        }));
        setChatSessions(sessions);
      } catch (_err) {
        setChatSessions([]);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [companionId, isLoggedIn]);

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Handle start conversation
  const handleStartConversation = () => {
    navigate(`/chat/${companionId}`);
  };

  // Handle resume chat session
  const handleResumeSession = (sessionId: string) => {
    navigate(`/chat/${companionId}?session=${sessionId}`);
  };

  // Handle delete conversation
  const handleDeleteSession = useCallback(
    async (e: React.MouseEvent, sessionId: string) => {
      e.stopPropagation();

      if (
        !window.confirm(
          t('companion.deleteConfirm', 'Are you sure you want to delete this conversation?'),
        )
      ) {
        return;
      }

      try {
        await deleteConversation(sessionId);
        setChatSessions((prev) => prev.filter((s) => s.id !== sessionId));
      } catch (_err) {
        alert(t('companion.deleteError', 'Failed to delete conversation'));
      }
    },
    [t],
  );

  // Open rename modal
  const openRenameModal = (e: React.MouseEvent, session: ChatSession) => {
    e.stopPropagation();
    setRenameModal({ isOpen: true, session, newTitle: session.title });
  };

  // Close rename modal
  const closeRenameModal = () => {
    setRenameModal({ isOpen: false, session: null, newTitle: '' });
  };

  // Handle rename conversation submit
  const handleRenameSubmit = async () => {
    if (
      !renameModal.session ||
      !renameModal.newTitle.trim() ||
      renameModal.newTitle === renameModal.session.title
    ) {
      closeRenameModal();
      return;
    }

    setIsRenaming(true);
    try {
      await updateConversation(renameModal.session.id, { title: renameModal.newTitle.trim() });
      setChatSessions((prev) =>
        prev.map((s) =>
          s.id === renameModal.session?.id ? { ...s, title: renameModal.newTitle.trim() } : s,
        ),
      );
      closeRenameModal();
    } catch (_err) {
      alert(t('companion.renameError', 'Failed to rename conversation'));
    } finally {
      setIsRenaming(false);
    }
  };

  // Handle rename input key press
  const handleRenameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      closeRenameModal();
    }
  };

  if (!companion) {
    return (
      <div className="companion-profile-page">
        <div className="bg-accent-shape" />
        <Header isLoggedIn={isLoggedIn} />
        <main className="companion-profile-main">
          <div className="companion-not-found">
            <h2>{t('companion.notFound', 'Companion not found')}</h2>
            <Link to="/">{t('companion.backToHome', 'Back to Home')}</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="companion-profile-page">
      {/* Background effects */}
      <div className="bg-accent-shape" />
      <div className="bg-sand-effect-1" />
      <div className="bg-sand-effect-2" />

      <Header isLoggedIn={isLoggedIn} />

      <main className="companion-profile-main">
        {/* Breadcrumb Navigation */}
        <nav className="breadcrumb-nav">
          <button type="button" className="back-btn" onClick={handleBack} aria-label="Go back">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <title>{t('common.back', 'Back')}</title>
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="breadcrumb-links">
            <Link to="/" className="breadcrumb-link">
              {t('companion.breadcrumb.chat', 'Chat')}
            </Link>
            <svg
              className="breadcrumb-separator"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <title>Separator</title>
              <path d="M9 18l6-6-6-6" />
            </svg>
            <span className="breadcrumb-current">{companion.displayName}</span>
          </div>
        </nav>

        {/* Start Conversation Button (Moved to top & Sticky) */}
        <div className="start-conversation-container">
          <button
            type="button"
            className="btn-start-conversation"
            onClick={handleStartConversation}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <title>{t('companion.startConversation', 'Start conversation')}</title>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>{t('companion.startConversation', 'Start conversation')}</span>
          </button>
        </div>

        {/* Hero Section with Avatar */}
        <section className="companion-hero">
          <div className="companion-avatar-container">
            <div className="avatar-glow" />
            <img src={companion.image} alt={companion.displayName} className="companion-avatar" />
            <div className="avatar-gradient" />
          </div>

          <div className="companion-hero-info">
            {/* About Label */}
            <div className="about-label-container">
              <div className="label-line" />
              <span className="about-label">{t('companion.about', 'About')}</span>
            </div>

            {/* Names */}
            <h1 className="companion-name-english">{companion.displayName}</h1>
          </div>
        </section>

        {/* Info Card - Roles & Lifespan */}
        <section className="companion-info-card">
          <div className="info-card-content">
            <div className="info-roles-section">
              <div className="info-section-header">
                <span className="info-section-title">{t('companion.roles', 'Roles')}</span>
              </div>
              <div className="roles-badges">
                {companion.displayTags.map((tag: string) => (
                  <span key={tag} className="role-badge">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="info-divider" />

            <div className="info-lifespan-section">
              <div className="info-section-header">
                <span className="info-section-title lifespan-title">
                  {t('companion.lifespan', 'Life span')}
                </span>
              </div>
              <span className="lifespan-value">{companion.lifespan}</span>
            </div>
          </div>
        </section>

        {/* Biography Section */}
        <section className="companion-biography">
          <div className="biography-header">
            <h3 className="biography-title">{t('companion.biography', 'Biography')}</h3>
          </div>
          <p className="biography-text">
            {companion.displayDescription ||
              t(
                'companion.defaultBio',
                `I am ${companion.displayName}, a companion of Prophet Muhammad (peace be upon him).
                            My life was devoted to truth, loyalty, and serving Allah's religion.
                            I stood with the Prophet through all trials and tribulations,
                            and my story is one of unwavering faith and dedication to Islam.`,
              )}
          </p>
        </section>

        {/* Chat History Section (if user is logged in) */}
        {isLoggedIn && (
          <section className="chat-history-section">
            <div className="chat-history-header">
              <h3>{t('companion.recentChats', 'Recent Chats')}</h3>
              <button
                type="button"
                className="add-chat-btn"
                onClick={handleStartConversation}
                aria-label={t('companion.startNewChat', 'Start new chat')}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <title>Add Icon</title>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>

            {isLoadingHistory ? (
              <div className="chat-sessions-loading">
                <span>{t('companion.loadingHistory', 'Loading chat history...')}</span>
              </div>
            ) : chatSessions.length === 0 ? (
              <div className="chat-sessions-empty">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#999"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <title>Empty History Icon</title>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <span>{t('companion.noChats', 'No conversations yet')}</span>
                <span className="empty-hint">
                  {t(
                    'companion.startChatHint',
                    'Start a conversation to learn from this companion',
                  )}
                </span>
              </div>
            ) : (
              <div className="chat-sessions-list">
                {chatSessions.map((session) => (
                  <button
                    type="button"
                    key={session.id}
                    className="chat-session-card"
                    onClick={() => handleResumeSession(session.id)}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <title>Chat Icon</title>
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    <div className="session-content">
                      <span className="session-title">{session.title}</span>
                      <span className="session-date">{session.date}</span>
                    </div>
                    <div className="session-actions">
                      <button
                        type="button"
                        className="session-action-btn"
                        onClick={(e) => openRenameModal(e, session)}
                        title={t('companion.rename', 'Rename')}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <title>Rename Icon</title>
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="session-action-btn delete"
                        onClick={(e) => handleDeleteSession(e, session.id)}
                        title={t('companion.delete', 'Delete')}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <title>Delete Icon</title>
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      <Footer />

      {/* Rename Modal */}
      {renameModal.isOpen && (
        <button
          type="button"
          className="rename-modal-overlay"
          onClick={closeRenameModal}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closeRenameModal();
          }}
          tabIndex={-1}
          aria-label="Close modal"
        >
          <div
            className="rename-modal"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rename-modal-title"
          >
            <div className="rename-modal-header">
              <h3 id="rename-modal-title">
                {t('companion.renameConversation', 'Rename Conversation')}
              </h3>
              <button
                type="button"
                className="rename-modal-close"
                onClick={closeRenameModal}
                aria-label="Close"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <title>Close Icon</title>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="rename-modal-body">
              <label htmlFor="rename-input">{t('companion.newName', 'New name')}</label>
              <input
                id="rename-input"
                type="text"
                value={renameModal.newTitle}
                onChange={(e) => setRenameModal((prev) => ({ ...prev, newTitle: e.target.value }))}
                onKeyDown={handleRenameKeyPress}
                placeholder={t('companion.enterNewName', 'Enter new name...')}
              />
            </div>
            <div className="rename-modal-footer">
              <button type="button" className="rename-modal-cancel" onClick={closeRenameModal}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="rename-modal-save"
                onClick={handleRenameSubmit}
                disabled={isRenaming || !renameModal.newTitle.trim()}
              >
                {isRenaming ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </button>
      )}
    </div>
  );
};

export default CompanionProfile;
